import type {
  PvpBattingSinceClear,
  PvpReviewList,
  PvpReviewPlayer,
  PvpReviewRule,
  PvpReviewStatus,
  UpdatePvpReviewBody,
} from '@/types/pvp';

/** The longest flag reason the backend takes (UpdatePvpReviewDto). */
export const PVP_REASON_MAX = 150;

// ParseUUIDPipe on the PUT path: anything else answers 400.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isPlayerId(text: string): boolean {
  return UUID_RE.test(text.trim());
}

/**
 * The PUT body. A flag carries the trimmed reason when there is one (stored as
 * "manual: <reason>"); a clear sends none, since the backend keeps no reason
 * for it.
 */
export function pvpReviewBody(flagged: boolean, reason?: string): UpdatePvpReviewBody {
  const why = reason?.trim();
  return flagged && why ? { flagged, reason: why } : { flagged };
}

/**
 * Who flagged, from the stored reason: an admin flag is "manual" or
 * "manual: <reason>"; the ledger writes the rule broken, e.g. "4.20 runs a
 * ball over 34 balls".
 */
export function flagReasonParts(reason?: string): { by: 'admin' | 'rule'; text: string } | null {
  if (reason === undefined) return null;
  if (reason === 'manual') return { by: 'admin', text: '' };
  if (reason.startsWith('manual: ')) return { by: 'admin', text: reason.slice('manual: '.length) };
  return { by: 'rule', text: reason };
}

/**
 * Which thresholds the batting since the last clear meets, as the ledger
 * judges it (pvpOutlier in pvp-review.ts): nothing before `minBalls` balls,
 * then each rate at or above its threshold. Worked out from the counts, not
 * the rates the API rounds to 2 places.
 */
export function ruleHits(
  c: PvpBattingSinceClear,
  rule: PvpReviewRule
): { judged: boolean; runsPerBall: boolean; sixRate: boolean } {
  if (c.balls <= 0 || c.balls < rule.minBalls) {
    return { judged: false, runsPerBall: false, sixRate: false };
  }
  return {
    judged: true,
    runsPerBall: c.runs / c.balls >= rule.runsPerBall,
    sixRate: c.sixes / c.balls >= rule.sixRate,
  };
}

/** The rule in one line, to follow "Flags a batter ...". */
export function ruleText(rule: PvpReviewRule): string {
  return `from ${rule.minBalls} balls since the last clear, at ${rule.runsPerBall} or more runs a ball or ${rule.sixRate} or more sixes a ball`;
}

/**
 * The list after a flag or clear, from the PUT answer (read on the primary).
 * Reloading instead would read the replica, which can still show the old flag.
 * Flagged list: a cleared player leaves, a flagged one is replaced in place or
 * added at the top. Everyone list: a clear restarts the counters below the
 * minimum, so the player leaves; otherwise the row is replaced in place, and a
 * player not listed waits for the next refresh (the list is sorted server-side).
 */
export function applyReviewChange(
  list: PvpReviewList,
  status: PvpReviewStatus,
  player: PvpReviewPlayer
): PvpReviewList {
  const others = list.items.filter((p) => p.userId !== player.userId);
  const listed = others.length !== list.items.length;
  const keep =
    status === 'flagged'
      ? player.flaggedAt !== undefined
      : player.sinceClear.balls >= list.rule.minBalls;
  if (!keep) return { ...list, items: others };
  if (listed) {
    return {
      ...list,
      items: list.items.map((p) => (p.userId === player.userId ? player : p)),
    };
  }
  return status === 'flagged' ? { ...list, items: [player, ...list.items] } : list;
}
